import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { COLORS } from '../../styles/tokens';
import { useUnreadCount } from '../../hooks/useUnreadCount';
import TabBadge from '../common/TabBadge';

const ICONS = {
  truck: <><path d="M2 6h11v10H2zM13 9h4l3 3v4h-7" /><circle cx="6" cy="17.5" r="1.8" /><circle cx="16.5" cy="17.5" r="1.8" /></>,
  plus:  <path d="M12 5v14M5 12h14" />,
  bell:  <path d="M6 17V11a6 6 0 1112 0v6l1.5 2h-15zM10 21h4" />,
  card:  <><rect x="3" y="5" width="18" height="14" rx="2.5" /><path d="M3 10h18" /></>,
  user:  <><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 3.5-6.5 8-6.5s8 2.5 8 6.5" /></>,
};

function Icon({ name, size = 22, strokeWidth = 1.8 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {ICONS[name]}
    </svg>
  );
}

// Sider som viser bunnmenyen (og skjuler toppmenyen) på mobil
const ROOT_PATHS = ['/orderer', '/orderer/invoices', '/orderer/projects', '/orderer/varsler', '/orderer/profil'];
export function isOrdererRootPath(pathname) {
  return ROOT_PATHS.includes(pathname.replace(/\/$/, '') || '/');
}

export function useNarrow() {
  const [narrow, setNarrow] = useState(window.innerWidth < 768);
  useEffect(() => {
    const h = () => setNarrow(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return narrow;
}

const TABS = [
  { key: 'orders',        label: 'Oppdrag',      icon: 'truck', to: '/orderer' },
  { key: 'invoices',      label: 'Fakturaer',    icon: 'card',  to: '/orderer/invoices' },
  { key: 'create',        label: 'Nytt oppdrag', icon: 'plus',  to: '/orderer/create' },
  { key: 'notifications', label: 'Varsler',      icon: 'bell',  to: '/orderer/varsler' },
  { key: 'profile',       label: 'Profil',       icon: 'user',  to: '/orderer/profil' },
];

// Bunnmeny for bestiller på mobil: Oppdrag, Fakturaer, Nytt oppdrag (stort gult pluss i midten), Varsler, Profil
export default function OrdererTabBar() {
  const narrow = useNarrow();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { unreadCount } = useUnreadCount();
  if (!narrow || !isOrdererRootPath(pathname)) return null;

  const path = pathname.replace(/\/$/, '');
  let active = 'orders';
  if (path === '/orderer/invoices') active = 'invoices';
  else if (path === '/orderer/varsler') active = 'notifications';
  else if (['/orderer/profil', '/orderer/projects'].includes(path)) active = 'profile';

  return (
    <nav aria-label="Hovedmeny" style={styles.bar}>
      {TABS.map(t => {
        const on = t.key === active;
        if (t.key === 'create') {
          return (
            <button key={t.key} onClick={() => navigate(t.to)} aria-label="Nytt oppdrag" style={styles.createTab}>
              <span style={styles.plus}><Icon name="plus" size={26} strokeWidth={2.4} /></span>
              <span style={{ ...styles.label, color: COLORS.textLight }}>{t.label}</span>
            </button>
          );
        }
        return (
          <button key={t.key} onClick={() => navigate(t.to)} aria-current={on ? 'page' : undefined}
                  aria-label={t.key === 'notifications' && unreadCount > 0 ? `Varsler, ${unreadCount} uleste` : undefined}
                  style={{ ...styles.tab, color: on ? COLORS.textDark : COLORS.textLight }}>
            <span style={{ ...styles.pill, background: on ? COLORS.accent : 'transparent', color: on ? '#1a1a2e' : 'inherit' }}>
              <Icon name={t.icon} />
              {t.key === 'notifications' && <TabBadge count={unreadCount} />}
            </span>
            <span style={{ ...styles.label, fontWeight: on ? 700 : 600 }}>{t.label}</span>
          </button>
        );
      })}
    </nav>
  );
}

const styles = {
  bar: {
    position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 300,
    display: 'flex', alignItems: 'flex-start', background: COLORS.white, borderTop: `1px solid ${COLORS.border}`,
    padding: '8px 8px max(10px, env(safe-area-inset-bottom))',
  },
  tab: {
    flex: 1, background: 'none', border: 'none', cursor: 'pointer',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, fontFamily: 'inherit', padding: 0,
  },
  createTab: {
    flex: 1, background: 'none', border: 'none', cursor: 'pointer', marginTop: -4,
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, fontFamily: 'inherit', padding: 0,
  },
  pill: { position: 'relative', width: 52, height: 30, borderRadius: 15, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  plus: {
    width: 52, height: 52, borderRadius: '50%', background: COLORS.accent, color: '#1a1a2e',
    display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 6px 14px rgba(253,193,73,0.45)',
  },
  label: { fontSize: 11, whiteSpace: 'nowrap' },
};

// Høyden bunnmenyen opptar, så sidene kan få plass under innholdet
export const ORDERER_TAB_BAR_PX = 84;
